import { BspOrderProviderSettings } from './bsp';
import { CognizantOrderProviderSettings } from './cognizant';
import { OrderServeOrderProviderSettings } from './order-serve';

export interface CartItemCustomisation {
  price: number;
  default: boolean;
  quantity: number;
  productId?: string;
  categoryId: string;
  defaultQuantity: number;
  providerData: {
    DefaultReason: number;
    ModifierAction: number;
  };
}

export interface CartItemProduct {
  id: string;
  name: string;
  type: string;
  price: number;
  providerData: any;
  // TODO: This needs to be typed
  comboProducts: any[];
  customisations: CartItemCustomisation[];
  filterId: string;
  posItemId: string;
}

export interface CartItem {
  id: string;
  uid: string;
  price: number;
  quantity: number;
  product: CartItemProduct;
}

export interface CartItemResponse extends CartItem {
  isAvailable: boolean;
}

export interface ProviderCheckIn {
  intent: number;
  originType: number;
  destination: number;
  orderType: 'DINE_IN' | 'TAKEAWAY';
}

// This should possibly be in `libraries/types/src/settings/index.ts`
export interface SessionSettings {
  timestamp: string;
  sessionId: string;
  kitchenId?: string | undefined;
  beaconNumber?: string | undefined;
  placeOrderAttempt?: number;
  customer?: {
    email: string;
    lastName: string;
    firstName: string;
    receiveMarketingEmail?: boolean;
  };
  receiptId?: string | undefined;
}

export interface PaymentSettings {
  amount: number;
  authCode: string;
  cardType: string;
  cardNumber: string;
  transactionId: string;
  paymentType: 'CASH' | 'CARD';
}

export interface LabsSettings {
  customRestaurantEndpoint?: string;
  customSaveOrderEndpoint?: string;
  customPreOrderEndpoint?: string;
  customPlaceOrderEndpoint?: string;
  customCancelOrderEndpoint?: string;
  customResetOrderEndpoint?: string;
  customPollingOrderEndpoint?: string;
}

// This should possibly be in `libraries/types/src/settings/index.ts`
export interface KioskSettings {
  serial: string;
  storeId: string;
  headers: unknown;
  timezone: string;
  organisation: string;
  storeFirestoreId: string;
  deviceFirestoreId: string;
  menuOrganisationId: string;
  organisationFirestoreId: string;
}

export interface ProviderSettings {
  menuId: number;
  intent: number;
  endpoint: string;
  originType: string;
  sendEmail: boolean;
  env: 'dev' | 'prod';
  useLoyalty: boolean;
  destination: number;
  companyCode: string;
  authBasicToken: string;
  orderModeDineIn: string;
  orderModeTakeaway: string;
  paymentMethodType: string;
  checkIns: ProviderCheckIn[];
  placeOrderIndividualItems: boolean;
  loyalty: {
    baseUrl: string;
    companyCode: string;
    applicationId: string;
    authBasicToken: string;
  };
}

export interface GenericSettings {
  mock: boolean;
  labs?: LabsSettings;
  kiosk: KioskSettings;
  provider?:
    | ProviderSettings
    | BspOrderProviderSettings
    | CognizantOrderProviderSettings
    | OrderServeOrderProviderSettings;
  providerType: string;
  apexLoggingProject: 'supersonic_development' | 'supersonic_production';
  interval?: IntervalConfiguration;
}

// Cart
// - This should probably be lifted to it's own file
// - The entire cart package could benefit from a TS conversion
export interface CartPayload {
  mode: 'DINE_IN' | 'TAKEAWAY';
  total: number;
  items: CartItem[];
}

export interface CartPayloadResponse extends CartPayload {
  items: CartItemResponse[];
}

export interface FinancialSummary {
  total: number;
  savedAmount: number;
  taxes: number;
}

export interface CalculateTaxAndTotalItemReferenceIds {
  ItemReferenceIds: string[];
}

export interface CalculateTaxAndTotalPromos {
  PromoId: number;
  ItemSelections: CalculateTaxAndTotalItemReferenceIds[];
}

export interface CalculateTaxAndTotalSubItem {
  Quantity: number;
  PosItemId: number;
  ReferenceId: number;
}

export interface CalculateTaxAndTotalItems {
  Quantity: number;
  PosItemId: number;
  ReferenceId: number;
  SubItems?: CalculateTaxAndTotalSubItem[];
}

export interface CalculateTaxAndTotalRequest {
  Promos?: CalculateTaxAndTotalPromos[];
  Customer: {
    FirstName: string;
    LastName: string;
    EMail: string;
  };
  Items: CalculateTaxAndTotalItems[];
  OrderMode: number;
  OrderSource: number;
}

export interface LoyaltyLoginPayload {
  code: string;
  sessionId: string;
  organisationId: string;
  kioskSerialKey: string;
  storeId: string;
  integrationStoreId: string;
}

// Pre order request
// - This was generated from logging in the UI, can probably be improved
export enum OrderStatus {
  PENDING = 'PENDING',
  CREATED = 'CREATED',
  SUBMITTED = 'SUBMITTED',
  FINISHED = 'FINISHED',
  ERROR = 'ERROR',
  CANCELED = 'CANCELED',
}

export interface PreOrderPayload {
  status: OrderStatus;
  cart: CartPayload;
  session: SessionSettings;
  settings: GenericSettings;
}

export interface SitePayload {
  session: SessionSettings;
  settings: GenericSettings;
}

export interface PreOrderResponsePayload extends PreOrderPayload {
  financialSummary: FinancialSummary;
  receiptId: string;
  calculateTaxAndTotalRequest: CalculateTaxAndTotalRequest;
}

export interface PlaceOrderPayload {
  status: OrderStatus;
  cart: CartPayload;
  session: SessionSettings;
  payment: PaymentSettings;
  settings: GenericSettings;
}

// Save order request
// - More optional fields on this type as it is built up over time
// - https://fingermarkglobal.atlassian.net/browse/S2-2172?focusedCommentId=21717
export interface SaveOrderPayload {
  orderNumber?: string;
  financialSummary?: string;
  cart?: CartPayload;
  session: SessionSettings;
  payment?: PaymentSettings;
  settings: GenericSettings;
}

export interface CancelOrderPayload {
  status?: string;
  cart: CartPayload;
  session: SessionSettings;
  payment: PaymentSettings;
  settings: GenericSettings;
}

export interface ResetOrderPayload {
  status?: string;
  cart: CartPayload;
  session: SessionSettings;
  payment: PaymentSettings;
  settings: GenericSettings;
}

export interface PlaceOrderWebhookCallbackHandlerPayloadResponse {
  kitchenId: string;
  status: OrderStatus;
  sessionId: string;
}

export interface PreOrderHandlerPayloadResponse {
  kitchenId: string;
  cart: CartPayloadResponse;
  financialSummary: FinancialSummary;
}

export interface PlaceOrderHandlerPayloadResponse {
  orderNumber: string;
}

export interface SitePayload {
  session: SessionSettings;
  settings: GenericSettings;
}

export interface IntervalConfiguration {
  autoRetry?: {
    webhookPolling: number;
  };
}

export type CustomerType =
  | 'yum-au-kfc'
  | 'kfc-yum-au'
  | 'yum-ca-kfc'
  | 'rb-au-tb'
  | 'rb-nz-tb'
  | 'rb-nz-kfc'
  | 'cfa-au-hj'
  | 'amr-uae-kfc'
  | 'amr-uae-hardees'
  | 'amr-egy-kfc'
  | 'amr-kwt-kfc'
  | 'amr-kwt-hardees'
  | 'amr-qat-kfc'
  | 'amr-qat-hardees'
  | 'amr-ksa-kfc'
  | 'amr-ksa-hardees'
  | 'amr-ksa-ph';

export type ProviderType =
  | 'aloha'
  | 'mycentral'
  | 'cognizant'
  | 'aloha-sdm'
  | 'fingermark'
  | 'local'
  | 'bsp'
  | 'order-serve'
  | 'store-connect'
  | 'oracle-micros'
  | 'integrationFabric';

export { PullMenuRequestPayloadDatasource } from './menu';

export interface PollingOrderProviderSettings {
  skipCheckIn: boolean;
  executeWebhookPolling: boolean;
  maxRetries: number;
  executeAutoRetry: boolean;
}
